import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Video, ArrowLeft, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';

// Zoom Meeting SDK Web - Client View
// Reutiliza el SDK cargado en ZoomSDKSandbox si ya está cargado.
const ZOOM_SDK_VERSION = '3.13.1';
const ZOOM_STYLESHEETS = [
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/css/bootstrap.css`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/css/react-select.css`,
];
const ZOOM_SDK_SCRIPTS = [
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/react.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/react-dom.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/redux.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/redux-thunk.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/lodash.min.js`,
  `https://source.zoom.us/${ZOOM_SDK_VERSION}/zoom-meeting-${ZOOM_SDK_VERSION}.min.js`,
];

const loadStylesheet = (href) => new Promise((resolve) => {
  if (document.querySelector(`link[href="${href}"]`)) return resolve();
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.onload = () => resolve();
  link.onerror = () => resolve();
  document.head.appendChild(link);
});

const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    if (existing.dataset.loaded === 'true') return resolve();
    existing.addEventListener('load', () => resolve());
    existing.addEventListener('error', () => reject(new Error(`No se pudo cargar: ${src}`)));
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.async = false;
  script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
  script.onerror = () => reject(new Error(`No se pudo cargar: ${src}`));
  document.head.appendChild(script);
});

let _zoomSdkLoadPromise = null;
const loadZoomClientSDK = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.ZoomMtg) return Promise.resolve(window.ZoomMtg);
  if (_zoomSdkLoadPromise) return _zoomSdkLoadPromise;
  _zoomSdkLoadPromise = (async () => {
    ZOOM_STYLESHEETS.forEach(loadStylesheet);
    for (const src of ZOOM_SDK_SCRIPTS) {
      // eslint-disable-next-line no-await-in-loop
      await loadScript(src);
    }
    if (!window.ZoomMtg) throw new Error('Zoom Client SDK no disponible en window');
    window.ZoomMtg.setZoomJSLib(`https://source.zoom.us/${ZOOM_SDK_VERSION}/lib`, '/av');
    window.ZoomMtg.preLoadWasm();
    window.ZoomMtg.prepareWebSDK();
    try { window.ZoomMtg.i18n.load('es-ES'); } catch (_e) {}
    try { window.ZoomMtg.i18n.reload('es-ES'); } catch (_e) {}
    return window.ZoomMtg;
  })();
  return _zoomSdkLoadPromise;
};

const showZoomRoot = () => {
  const el = document.getElementById('zmmtg-root');
  if (el) {
    el.style.display = 'block';
    el.style.position = 'fixed';
    el.style.inset = '0';
    el.style.zIndex = '9999';
  }
};
const hideZoomRoot = () => {
  const el = document.getElementById('zmmtg-root');
  if (el) el.style.display = 'none';
};

const ClaseEnVivo = () => {
  const { API, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const claseId = searchParams.get('clase_id');

  const [clase, setClase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [joined, setJoined] = useState(false);

  // Cargar datos de la clase
  useEffect(() => {
    if (!claseId) {
      setLoadError('Falta el ID de la clase');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/clases/${claseId}/en-vivo-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setClase(res.data);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.detail || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, claseId]);

  // Cleanup SDK al desmontar
  useEffect(() => {
    return () => {
      try { if (window.ZoomMtg) window.ZoomMtg.leaveMeeting({}); } catch (_e) {}
      hideZoomRoot();
    };
  }, []);

  const volverDashboard = () => {
    try { if (window.ZoomMtg) window.ZoomMtg.leaveMeeting({}); } catch (_e) {}
    hideZoomRoot();
    navigate('/app/dashboard');
  };

  const abrirZoomNormal = () => {
    const zoomUrl = clase?.tipo_participacion === 'estudiante'
      ? clase?.zoom_join_url
      : (clase?.zoom_start_url || clase?.zoom_join_url);
    if (zoomUrl) window.open(zoomUrl, '_blank');
  };

  const entrarAClase = useCallback(async () => {
    if (!clase?.zoom_meeting_id) {
      toast.error('Esta clase no tiene una reunión de Zoom configurada');
      return;
    }
    setJoining(true);
    setSdkError(null);

    try {
      // 1) Marcar asistencia (no bloqueante si falla)
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/profesor/registrar-asistencia-clase`,
          { clase_id: clase.id, clase_nombre: clase.titulo },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('✅ Asistencia registrada');
      } catch (e) {
        console.warn('No se pudo registrar asistencia:', e);
      }

      // 2) Cargar SDK Client View
      const ZoomMtg = await loadZoomClientSDK();

      // 3) Determinar rol: admin/profesor = host, estudiante = participant
      const esHost = clase.tipo_participacion === 'admin' || clase.tipo_participacion === 'profesor';
      const role = esHost ? 1 : 0;

      // 4) Pedir signature + password + zak al backend
      const token = localStorage.getItem('token');
      const sigResp = await axios.post(`${API}/zoom-sdk-test/signature`,
        { meeting_number: String(clase.zoom_meeting_id), role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { signature, sdk_key, zak, password } = sigResp.data;
      if (!signature || !sdk_key) throw new Error('Backend no devolvió signature/sdk_key');

      showZoomRoot();

      const userName = user ? `${user.nombre || ''} ${user.apellido || ''}`.trim() : 'Participante';

      await new Promise((resolve, reject) => {
        ZoomMtg.init({
          leaveUrl: `${window.location.origin}/app/dashboard`,
          patchJsMedia: true,
          isSupportAV: true,
          success: () => {
            ZoomMtg.join({
              signature,
              sdkKey: sdk_key,
              meetingNumber: String(clase.zoom_meeting_id),
              passWord: password || '',
              userName: userName || 'Participante',
              zak: esHost ? (zak || undefined) : undefined,
              success: () => {
                setJoined(true);
                resolve();
              },
              error: (err) => {
                console.error('Join error:', err);
                reject(new Error(err?.reason || err?.errorMessage || 'Error al unirse'));
              },
            });
          },
          error: (err) => {
            console.error('Init error:', err);
            reject(new Error(err?.reason || err?.errorMessage || 'Error al inicializar SDK'));
          },
        });
      });
    } catch (err) {
      console.error('Error Client View:', err);
      hideZoomRoot();
      const msg = err?.message || 'No se pudo cargar el modo embebido';
      setSdkError(msg);
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  }, [API, clase, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (loadError || !clase) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">No se pudo cargar la clase</h2>
            <p className="text-gray-600 mb-4">{loadError || 'Clase no encontrada'}</p>
            <Button onClick={() => navigate('/app/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const esHost = clase.tipo_participacion === 'admin' || clase.tipo_participacion === 'profesor';

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto space-y-4" data-testid="clase-en-vivo-page">
      <Button variant="ghost" onClick={() => navigate('/app/dashboard')} className="text-sm">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver al dashboard
      </Button>

      <Card className="overflow-hidden">
        {clase.banner_url && (
          <div className="relative h-40">
            <img src={clase.banner_url} alt={clase.titulo} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h1 className="text-white text-xl font-bold">{clase.titulo}</h1>
              <p className="text-white/90 text-sm">Prof. {clase.profesor_nombre}</p>
            </div>
          </div>
        )}

        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shrink-0">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {esHost ? '🎓 Vas a iniciar la clase' : '📚 Vas a entrar a clase'}
              </p>
              <p className="text-sm text-gray-500">
                Modo embebido — la clase se abre dentro de IBA Connect, sin salir a Zoom.
              </p>
            </div>
          </div>

          {sdkError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-2">
              <div className="flex items-start gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong>No pudimos cargar el modo embebido.</strong>
                  <p className="text-xs mt-1 opacity-80">{sdkError}</p>
                </div>
              </div>
              <Button
                onClick={abrirZoomNormal}
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100"
                data-testid="fallback-zoom-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Abrir en Zoom normal (fallback)
              </Button>
            </div>
          )}

          <Button
            onClick={entrarAClase}
            disabled={joining || joined}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-6 text-base shadow-md"
            data-testid="entrar-clase-btn"
          >
            {joining ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando clase...</>
            ) : joined ? (
              <><Video className="w-5 h-5 mr-2" /> En clase</>
            ) : (
              <><Video className="w-5 h-5 mr-2" /> Entrar a Clase</>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Al entrar, tu asistencia queda registrada automáticamente.
          </p>
        </CardContent>
      </Card>

      {/* Botón Salir visible solo cuando estamos en la llamada embebida */}
      {joined && (
        <div className="fixed top-3 right-3 z-[10001] flex items-center gap-2">
          <div className="px-3 py-1 rounded-full bg-black/70 text-white text-xs backdrop-blur-sm">
            {esHost ? '🎓 Anfitrión' : '📚 Estudiante'} · {clase.titulo}
          </div>
          <Button
            onClick={volverDashboard}
            size="sm"
            variant="destructive"
            data-testid="salir-clase-btn"
          >
            Salir
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClaseEnVivo;
